import React from 'react';
import { Row, Col, Card, Typography, Tag, Button, Space, Divider } from 'antd';
import { 
  CodeOutlined, 
  LinkOutlined, 
  GlobalOutlined,
  LaptopOutlined,
  MobileOutlined,
  DatabaseOutlined,
  SecurityScanOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  RocketOutlined,
  HeartOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Projects = () => {
  const featuredProjects = [
    {
      id: 1,
      title: 'Umotrack - Healthcare Logistics Platform',
      client: 'Global Fund & Government Partners',
      period: '2020 - Present',
      type: 'Enterprise Healthcare System',
      status: 'Active & Expanding',
      description: 'Mission-critical logistics platform for medical sample and supply transportation across multiple African countries. Currently serving Sierra Leone and Uganda with expansion to additional countries.',
      features: [
        'Real-time tracking of medical samples and supplies',
        'Multi-country logistics coordination system',
        'Government partnership management portal',
        'Compliance and regulatory reporting',
        'Mobile-first tracking interface for field workers',
        'Advanced analytics and reporting dashboard',
        'Integration with healthcare systems and laboratories',
        'Emergency response and priority handling'
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'Express.js', 'Redis', 'Docker', 'AWS', 'Microservices'],
      website: 'https://sl-tracker.umojaimcouriers.com/',
      achievements: [
        'Successfully delivered 3,000,000+ medical samples across Sierra Leone and Uganda',
        'Led development team as Chief Software Engineer for mission-critical healthcare platform',
        'Expanded operations to multiple countries with robust, scalable architecture',
        'Maintained 99.9% uptime for critical healthcare logistics operations',
        'Implemented real-time tracking reducing delivery time by 40%'
      ],
      services: ['Enterprise Software Development', 'Healthcare Technology', 'Logistics Management', 'Government Solutions', 'Real-time Tracking Systems'],
      impact: 'Critical infrastructure supporting healthcare delivery and disease surveillance across Africa',
      icon: <HeartOutlined />
    },
    {
      id: 2,
      title: 'PI Technologies - Fintech SACCO & Microfinance Platform',
      client: 'PI Technologies Uganda Ltd (Co-founder)',
      period: '2019 - Present',
      type: 'Enterprise Fintech',
      status: 'Active & Expanding',
      description: 'Co-founded PI Technologies, a leading fintech company specializing in SACCO and microfinance management systems. Developed multitenant applications serving over 1,000,000 Ugandans across multiple financial institutions.',
      features: [
        'PiSacco Management System - specialized software for SACCO institutions supporting VSLA',
        'Pi Microfinance Management System for investment clubs and money lending institutions',
        'Pi Collections as a Service - collections and payout payment solution',
        'Pi Biz On Wheels Management App - mobile business management platform',
        'Bulk collections and payouts payments solution',
        'Multi-branch capabilities with local, cloud and hybrid deployments',
        'Field collections app for loan applications and collections',
        'Email & SMS alerts with system notifications and reminders'
      ],
      technologies: ['PHP', 'Laravel', 'MySQL', 'JavaScript', 'React', 'Node.js', 'AWS', 'Multitenant Architecture', 'REST APIs'],
      website: 'https://pitech.co.ug',
      achievements: [
        'Co-founded fintech company serving over 1,000,000 Ugandans across multiple financial institutions',
        'Built multitenant SaaS architecture processing 1+ billion UGX in transactions',
        'Developed comprehensive financial management systems for SACCOs and microfinance institutions',
        'Implemented 24/7 technical support and managed IT services for financial institutions',
        'Created scalable platform serving districts across Uganda with deployed software'
      ],
      services: ['Fintech Development', 'SACCO Management Systems', 'Microfinance Solutions', 'Multitenant Architecture', 'Financial Technology'],
      impact: 'Transforming financial services in Uganda through innovative SACCO and microfinance management systems',
      icon: <DatabaseOutlined />
    },
    {
      id: 3,
      title: 'AnidasoHub - AI-Powered Clinical Trials Voice Agent',
      client: 'AnidasoHub, Inc (Co-founder)',
      period: '2024 - Present',
      type: 'AI Healthcare Technology',
      status: 'Active Development',
      description: 'Co-founded AnidasoHub, Inc, a startup developing AI-powered voice agents for clinical trials. The platform uses advanced natural language processing to streamline patient recruitment and engagement in clinical research.',
      features: [
        'AI-powered voice agent for clinical trial recruitment',
        'Natural language processing for patient screening',
        'Multi-language support for diverse patient populations',
        'Integration with clinical trial management systems',
        'Real-time patient engagement and follow-up',
        'Analytics dashboard for trial coordinators',
        'HIPAA-compliant data handling and storage',
        'Scalable cloud infrastructure for global deployment'
      ],
      technologies: ['AI/ML', 'Natural Language Processing', 'Python', 'React', 'Node.js', 'AWS', 'Docker', 'Machine Learning'],
      website: 'https://anidasohub.com',
      achievements: [
        'Co-founded startup focused on AI-powered healthcare solutions',
        'Developed innovative voice agent technology for clinical trials',
        'Built scalable platform for patient recruitment and engagement',
        'Implemented HIPAA-compliant data processing systems',
        'Created user-friendly interface for trial coordinators'
      ],
      services: ['AI Development', 'Healthcare Technology', 'Startup Leadership', 'Clinical Research', 'Voice AI'],
      impact: 'Revolutionizing clinical trial recruitment through AI-powered voice technology',
      icon: <RocketOutlined />
    },
    {
      id: 4,
      title: 'Pamoja Group Ltd - Multi-Service Platform',
      client: 'Pamoja Group Ltd',
      period: '2019',
      type: 'Web Development',
      status: 'Completed',
      description: 'Comprehensive business solution platform integrating software development, printing & branding, cleaning services, and general supplies management.',
      features: [
        'Multi-service portfolio management system',
        'Client management and project tracking',
        'Invoice generation and payment processing',
        'Service request and scheduling system',
        'Inventory management for general supplies',
        'Customer portal for service requests'
      ],
      technologies: ['PHP', 'Laravel', 'JavaScript', 'MySQL', 'Bootstrap', 'AJAX'],
      website: 'https://pamojagroupltd.com/',
      achievements: [
        'Delivered invoicing system processing 10,000+ monthly transactions with 99.5% accuracy',
        'Developed e-commerce POS app increasing sales processing speed by 50%',
        'Built salon management platform serving 15+ businesses, improving scheduling efficiency by 40%'
      ],
      services: ['Software Development', 'Web Design', 'E-commerce Solutions', 'Business Management Systems'],
      icon: <ShoppingCartOutlined />
    },
    {
      id: 5,
      title: 'Canon Innovations - IT Services Platform',
      client: 'Canon Innovations Ltd',
      period: 'February 2019 - Present',
      type: 'Enterprise Solutions',
      status: 'Ongoing',
      description: 'Leading development of enterprise applications and IT infrastructure management for Canon Innovations\' comprehensive IT services including hardware/software support, cybersecurity, and data management.',
      features: [
        'Hardware and software sales management system',
        'Data management and analytics dashboard',
        'Cybersecurity monitoring and reporting tools',
        'Network design and management interface',
        'IT consulting service management platform',
        'Client support ticket system',
        'Managed services automation'
      ],
      technologies: ['Java', 'Python', 'Django', 'PostgreSQL', 'AWS', 'Linux', 'Docker', 'React.js'],
      website: 'https://canoninnovations.com/',
      achievements: [
        'Deployed financial systems across 8 branches serving 2,000+ members with 95% adoption rate within 3 months',
        'Implemented security protocols ensuring 100% compliance with financial standards and zero breach incidents',
        'Trained 50+ staff members on system usage, reducing support tickets by 70%',
        'Developed on-demand service platform serving 1,000+ daily users with 4.8-star rating',
        'Built school management system supporting 25+ institutions and 5,000+ students, reducing admin costs by 60%'
      ],
      services: ['Enterprise Software Development', 'IT Infrastructure', 'Cybersecurity', 'Data Management', 'Network Solutions'],
      icon: <SecurityScanOutlined />
    }
  ];

  return (
    <section id="projects" className="section section-light">
      <div className="container">
        <div className="section-header">
          <Title level={2} className="section-title">Featured Projects</Title>
          <Paragraph className="section-subtitle">Major projects and platforms I've developed and co-founded</Paragraph>
        </div>

        <Row gutter={[32, 32]}>
          {featuredProjects.map((project, index) => (
            <Col key={project.id} xs={24} lg={12}>
              <Card
                hoverable
                style={{ 
                  height: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
                cover={
                  <div style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                      {project.icon}
                    </div>
                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                      {project.title}
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {project.client} • {project.period}
                    </Text>
                  </div>
                }
              >
                <div style={{ marginBottom: '16px' }}>
                  <Space wrap>
                    <Tag color="blue">{project.type}</Tag>
                    <Tag color={project.status === 'Completed' ? "green" : project.status === 'Active & Expanding' ? "gold" : "orange"}>
                      {project.status}
                    </Tag>
                  </Space>
                </div>

                <Paragraph style={{ marginBottom: '16px' }}>
                  {project.description}
                </Paragraph>

                <div style={{ marginBottom: '16px' }}>
                  <Title level={5} style={{ marginBottom: '8px' }}>Key Achievements:</Title>
                  <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                    {project.achievements.slice(0, 3).map((achievement, idx) => (
                      <li key={idx} style={{ marginBottom: '4px', fontSize: '14px' }}>
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <Title level={5} style={{ marginBottom: '8px' }}>Technologies:</Title>
                  <div>
                    {project.technologies.map((tech, idx) => (
                      <Tag key={idx} size="small" color="blue" style={{ marginBottom: '4px' }}>
                        {tech}
                      </Tag>
                    ))}
                  </div>
                </div>

                <Divider />

                <div style={{ textAlign: 'center' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<LinkOutlined />}
                    href={project.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      width: '100%',
                      height: '40px',
                      fontSize: '14px'
                    }}
                  >
                    View Live Project
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

export default Projects;